import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TableChartIcon from '@mui/icons-material/TableChart';
import MessageBase from './MessageBase';
import FormattedText from '../FormattedText';
import LoadingIndicator from './LoadingIndicator';
import DownloadButton from './DownloadButton';
import useDownloadHandler from '../../hooks/useDownloadHandler';

// Cache global para documentos ya cargados
const globalDocumentCache = new Map();

// Función para extraer el nombre de archivo de la URL o del texto
const extractFileName = (url, text) => {
    // Si tenemos texto que parece ser un nombre de archivo
    if (text && (text.includes('.') || (text.startsWith('"') && text.endsWith('"')))) {
        // Si está entre comillas, quitar las comillas
        return text.startsWith('"') ? text.substring(1, text.length - 1) : text;
    }
    
    // Si no, extraer de la URL
    if (url) {
        // Decodifica la URL por si tiene caracteres especiales
        const decodedUrl = decodeURIComponent(url);
        const urlParts = decodedUrl.split('/');
        return urlParts[urlParts.length - 1]; // Último segmento de la URL
    }
    
    return 'Documento';
};

// Función para determinar el tipo de archivo
const determineFileType = (fileName) => {
    if (!fileName) return null;
    
    const extension = fileName.split('.').pop().toLowerCase();
    
    if (extension === 'pdf') return 'application/pdf';
    if (['doc', 'docx'].includes(extension)) return 'application/msword';
    if (['xls', 'xlsx', 'csv'].includes(extension)) return 'application/vnd.ms-excel';
    if (['txt'].includes(extension)) return 'text/plain';
    
    return 'application/octet-stream'; // Tipo genérico
};

const DocumentMessage = ({ text, sender, timestamp, status, media_url, caption, id }) => {
    // Identificador único para el documento
    const docId = id || `doc-${media_url?.substring(media_url?.lastIndexOf('/') + 1) || Date.now()}`;
    
    // Verificar inmediatamente si el documento ya está en caché
    const isDocumentCached = useMemo(() => globalDocumentCache.has(media_url), [media_url]);
    
    // El estado loading comienza como false si el documento ya está en caché
    const [loading, setLoading] = useState(!isDocumentCached);
    const originalUrlRef = useRef(media_url);
    
    // Generar datos faltantes
    const fileName = extractFileName(media_url, text);
    const fileType = determineFileType(fileName);
    
    // Determinar la extensión para la descarga
    const getFileExtension = () => {
        const parts = fileName.split('.');
        return parts.length > 1 ? `.${parts.pop()}` : '.pdf'; // Valor predeterminado
    };
    
    // Usar el hook personalizado para la descarga
    const { isDownloading, handleDownload } = useDownloadHandler(
        media_url, 
        getFileExtension(), 
        'documento', 
        caption || fileName
    );
    
    // Efecto para manejar la carga del documento
    useEffect(() => {
        // Si ya está en caché, no hacemos nada
        if (globalDocumentCache.has(media_url)) {
            setLoading(false);
            return;
        }
        
        // Verificar si la URL cambió
        if (originalUrlRef.current !== media_url) {
            console.warn('La URL del documento cambió para el mismo mensaje:', docId);
            originalUrlRef.current = media_url;
        }
        
        // Para casos donde media_url es undefined o null
        if (!media_url) {
            setLoading(false);
            return;
        }
        
        // Simulamos la carga del documento
        const timer = setTimeout(() => {
            globalDocumentCache.set(media_url, true);
            setLoading(false);
        }, 500);
        
        return () => clearTimeout(timer);
    }, [media_url, docId]);
    
    // Efecto para detectar actualizaciones de estado desde Ably
    useEffect(() => {
        if (status && globalDocumentCache.has(media_url)) {
            setLoading(false);
        }
    }, [status, media_url]);
    
    // Función para determinar el ícono según el tipo de archivo
    const getFileIcon = () => {
        if (!fileType) return <InsertDriveFileIcon fontSize="large" />;
        
        if (fileType.includes('pdf')) {
            return <PictureAsPdfIcon fontSize="large" />;
        } else if (fileType.includes('word') || fileType.includes('msword')) {
            return <DescriptionIcon fontSize="large" />;
        } else if (fileType.includes('excel') || fileType.includes('csv')) {
            return <TableChartIcon fontSize="large" />;
        } else if (fileType.includes('text')) {
            return <TextSnippetIcon fontSize="large" />;
        } else {
            return <InsertDriveFileIcon fontSize="large" />;
        }
    };
    
    // Función para mostrar un tamaño aproximado (opcional)
    const getFileSize = () => {
        // Esto es solo una estimación visual ya que no tenemos el dato real
        const extension = getFileExtension().toLowerCase();
        
        if (['.pdf', '.docx', '.xlsx'].includes(extension)) {
            return '~ Documento'; // No mostrar tamaño estimado
        } else if (['.csv', '.txt'].includes(extension)) {
            return '~ Texto';
        } else {
            return '';
        }
    };
    
    return (
        <MessageBase sender={sender} timestamp={timestamp} status={status}>
            <Box sx={{ paddingBottom: '15px' }}>
                {/* Contenedor del documento */}
                <Box sx={{ position: 'relative' }}>
                    {/* Indicador de carga - solo visible cuando está cargando */}
                    {loading && (
                        <LoadingIndicator 
                            contentType="documento"
                            isLoading={loading}
                            color={sender === 'user' ? 'inherit' : 'primary'}
                            textColor={sender === 'user' ? 'white' : 'text.secondary'}
                        />
                    )}
                    
                    {/* Contenido del documento - con opacity cuando está cargando */}
                    <Box 
                        sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: sender === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                            borderRadius: '8px',
                            padding: '12px',
                            gap: 2,
                            opacity: loading ? 0 : 1,
                            transition: 'opacity 0.3s'
                        }}
                    >
                        {/* Icono de tipo de archivo */}
                        <Box sx={{ 
                            color: sender === 'user' ? 'white' : 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {getFileIcon()}
                        </Box>
                        
                        {/* Información del archivo */}
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {fileName}
                            </Typography>
                            
                            <Typography variant="caption" sx={{ color: sender === 'user' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                                {getFileSize()}
                            </Typography>
                        </Box>
                        
                        {/* Botón de descarga */}
                        <DownloadButton 
                            onDownload={handleDownload}
                            isDownloading={isDownloading}
                            fileType="documento"
                            size="small"
                        />
                    </Box>
                </Box>
                
                {/* Si hay texto adicional (caption), mostrarlo */}
                {caption && caption !== fileName && (
                    <Box sx={{ mt: 1 }}>
                        <FormattedText text={caption} />
                    </Box>
                )}
            </Box>
        </MessageBase>
    );
};

export default React.memo(DocumentMessage);